import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import {
  fetchAiHiringDripCampaigns,
  stopAiHiringDripCampaign,
} from "../../utils/api";

const statusMeta = {
  active: {
    label: "🟡 Active - currently in drip",
    className: "ai-tag ai-tag--warning",
  },
  responded: {
    label: "✅ Responded - replied YES",
    className: "ai-tag ai-tag--success",
  },
  expired: {
    label: "⛔ Expired - no response after 5 attempts",
    className: "ai-tag ai-tag--danger",
  },
};

const attemptsStyle = (attempts, maxAttempts = 5) => {
  if (attempts >= maxAttempts) {
    return { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" };
  }
  return { background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" };
};

const relativeTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60 * 1000) return "just now";
  const mins = Math.floor(diffMs / (60 * 1000));
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const AiHiringDripCampaigns = (props) => {
  const navigate = useNavigate();
  const locationId = props.activeLocation?.id || props.activeLocation?._id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ active: 0, responded: 0, expired: 0 });
  const [stopLoadingCandidateId, setStopLoadingCandidateId] = useState("");

  const load = async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const data = await fetchAiHiringDripCampaigns({ locationId });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setStats(data?.stats || { active: 0, responded: 0, expired: 0 });
      setError("");
    } catch (err) {
      setItems([]);
      setStats({ active: 0, responded: 0, expired: 0 });
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load drip campaigns.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          new Date(b.lastAttemptAt || b.createdAt || 0).getTime() -
          new Date(a.lastAttemptAt || a.createdAt || 0).getTime(),
      ),
    [items],
  );

  const onStopCampaign = async (candidateId) => {
    if (!candidateId) return;
    const ok = window.confirm("Stop this drip campaign for the candidate?");
    if (!ok) return;
    try {
      setStopLoadingCandidateId(candidateId);
      await stopAiHiringDripCampaign({ candidateId });
      await load();
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to stop campaign.",
      );
    } finally {
      setStopLoadingCandidateId("");
    }
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="AI Hiring - Drip Campaigns"
        subtitle="Track candidates in automated follow-up campaigns."
        icon={
          <span role="img" aria-label="Drip Campaigns">
            📨
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        {error && <div className="ai-alert ai-alert-error">{error}</div>}
        {!locationId ? (
          <div className="ai-empty">Select a location to view drip campaigns.</div>
        ) : (
          <>
            <div className="ai-stat-grid">
              <div className="ai-stat-card">
                <div className="ai-stat-label">Active</div>
                <div className="ai-stat-value">{Number(stats.active || 0)}</div>
                <div className="ai-stat-foot">Currently in drip</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Responded</div>
                <div className="ai-stat-value">{Number(stats.responded || 0)}</div>
                <div className="ai-stat-foot">Replied YES</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Expired</div>
                <div className="ai-stat-value">{Number(stats.expired || 0)}</div>
                <div className="ai-stat-foot">No response after 5 attempts</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Total Campaigns</div>
                <div className="ai-stat-value">{sorted.length}</div>
                <div className="ai-stat-foot">All statuses</div>
              </div>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <h3 className="ai-card-title">Drip Campaigns</h3>
              </div>
              {loading ? (
                <div className="ai-empty">Loading campaigns...</div>
              ) : sorted.length ? (
                <div className="ai-table-wrapper">
                  <table className="ai-table">
                    <thead>
                      <tr>
                        <th>Candidate</th>
                        <th>Job role</th>
                        <th>Attempts</th>
                        <th>Status</th>
                        <th>Last contacted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((item) => {
                        const attempts = Number(item.attempts || 0);
                        const maxAttempts = Number(item.maxAttempts || 5);
                        return (
                          <tr key={item._id}>
                            <td>
                              <div style={{ fontWeight: 700 }}>
                                {item.candidateName || "Candidate"}
                              </div>
                              <div className="ai-muted">
                                {item.candidateEmail || "No email"}
                              </div>
                            </td>
                            <td>{item.jobTitle || "--"}</td>
                            <td>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  borderRadius: 999,
                                  padding: "4px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  ...attemptsStyle(attempts, maxAttempts),
                                }}
                              >
                                {attempts}/{maxAttempts}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  statusMeta[item.status]?.className || "ai-tag ai-tag--neutral"
                                }
                              >
                                {statusMeta[item.status]?.label || item.status || "Unknown"}
                              </span>
                            </td>
                            <td>{relativeTime(item.lastAttemptAt || item.createdAt)}</td>
                            <td>
                              <div className="ai-decision-actions">
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-warning"
                                  onClick={() => onStopCampaign(item.candidateId)}
                                  disabled={
                                    item.status !== "active" ||
                                    stopLoadingCandidateId === item.candidateId
                                  }
                                >
                                  {stopLoadingCandidateId === item.candidateId
                                    ? "Stopping..."
                                    : "Stop Campaign"}
                                </button>
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-secondary"
                                  onClick={() =>
                                    navigate(
                                      `/ai-hiring/candidates?candidateId=${encodeURIComponent(
                                        item.candidateId,
                                      )}`,
                                    )
                                  }
                                >
                                  View Candidate
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ai-empty">No drip campaigns found.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiHiringDripCampaigns;
